import { HttpEventType } from '@angular/common/http';
import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { MessageService } from 'primeng/api';
import { take } from 'rxjs/operators';
import { GerinusBD } from 'src/app/app-core/lib/gerinus-bd.component';
import { GlobalService } from 'src/app/app-core/lib/global.service';
import { CryptoService } from 'src/app/app-core/services/crypto.service';
import { InternalProcessAttachmentsService } from 'src/app/app-core/services/internal-process-attachments.service';
import { InternalProcessService } from 'src/app/app-core/services/internal-process.service';
import { ProcessService } from 'src/app/app-core/services/process.service';
import { RequestAttachmentService } from 'src/app/app-core/services/requestAttachment.service';
import { UploadService } from 'src/app/app-core/services/upload.service';
import { UserService } from 'src/app/app-core/services/user.service';
import { IGetEntityProccessResponse } from 'src/app/interfaces/getEntity/getEtityProccessResponse';
import { IProccess } from 'src/app/interfaces/proccess.interface';
import Swal from 'sweetalert2';

@Component({
    selector: 'app-dropzone',
    templateUrl: './dropzone.component.html',
    styleUrls: ['./dropzone.component.scss'],
    providers: [GlobalService, UploadService, RequestAttachmentService, InternalProcessAttachmentsService, ProcessService, MessageService],
})
export class DropzoneComponent extends GerinusBD implements OnInit, OnChanges, OnDestroy {
    
    @Input() title: string;
    @Input() description: string;
    @Input() organization?: string;
    @Input() variant: string = 'inline';
    @Input() mimeTypes: string[];
    @Input() isDisabled: boolean;
    @Input() typeDocument: string;
    @Input() files: any[];
    @Input() showDropzone: boolean = true;
    @Input() showDownloadButton: boolean = true;
    @Input() showDeleteButton: boolean = true;
    @Input() showPreview: boolean = true;
    @Input() showBlockUi: boolean = true;
    @Input() showSwalAfterUpload: boolean = true;
    @Input() limitFiles: number;
    @Input() showTime = true;
    @Input() maxFileSize: number = 40 * 1024 * 1024; // Padrão 40MB
    @Input() isRequired: boolean = true;

    @Input() mappedFields: string[]; // Tera que passar as colunas respectivamente, caso não sejam anexos da solicitação: id, nome, anexo, status, data. Exemplo: ["PIA_CodigoProcessoAnexo", "PIA_Nome", "PIA_Anexo", "PIA_Status", "PIA_DataUpload"]
    @Input() canceledStatus = 0;
    @Input() removeFilesAfterSave = true;
    @Input() removeFilesAfterCancel = true;
    @Output() eventUploadedFile: any = new EventEmitter();
    @Output() deleteUploadedFile: any = new EventEmitter();
    @Output() cancelFileUploaded = new EventEmitter();
    @Input() styleVariant: 'primary' | 'secondary' = 'primary';
    @Input() type: 'process' | 'internal-process';
    @Input() showAdminDeleteButton: boolean = false;
    @Output() adminDeleteFile = new EventEmitter();
    @Input() showProgressBar: boolean = true;

    public swal = Swal;
    public loading: boolean = false;
    public isUploading: boolean = false;
    public uploadProgress: number = 0;
    public uploadProgressUnknown: boolean = false;
    public uploadingFileName: string = '';
    public downloadingFiles: { [key: string]: boolean } = {};
    private uploadSubscription?: Subscription;
    private uploadingFile?: File;
    public process: IProccess;
    public responsibles: any = [];
    public internalProcessId: number;
    public processId: number;
    public encryptedId: string;
    public internalProcess;

    allFiles: any = [];

    constructor(
        public global: GlobalService,
        public uploadService: UploadService,
        public anexoService: RequestAttachmentService,
        public userService: UserService,
        public internalAnexoService: InternalProcessAttachmentsService,
        public processService: ProcessService,
        public route: ActivatedRoute,
        private cryptoService: CryptoService,
        public internalProcessService: InternalProcessService,
        private messageService: MessageService,
    ) {
        super(global, anexoService);
        this.afterCreate();
    }

    ngOnInit() {
        if (this.files) {
          this.renameFiles(this.files);
        }
      
        this.route.paramMap.pipe(take(1)).subscribe((params) => {
          const encrypted = params.get('id');
          if (!encrypted) return;
      
          try {
            const decryptedId = Number(this.cryptoService.decrypt(encrypted));
            this.encryptedId = encrypted;
      
            if (this.type === 'internal-process') {
              this.internalProcessId = decryptedId;
              this.initializeDataIsInternalProcess(decryptedId);
            } else if (this.type === 'process') {
              this.processId = decryptedId;
              this.initializeDataIsProcess(decryptedId);
            } else {
              console.warn('Tipo de processo desconhecido:', this.type);
            }
          } catch (error) {
            console.error('Erro ao descriptografar o ID:', error);
          }
        });
      }

    private initializeDataIsInternalProcess(internalProcessId: number) {
        this.getInternalProcess(internalProcessId);
        this.searchResponsiblesIsInternalProcess();
    }

    private initializeDataIsProcess(processoId: number) {
        this.getProcess(processoId);
        this.searchResponsiblesIsProcess();
    }
    

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.files && this.files) {
            this.renameFiles(this.files);
        }
    }

    public ngOnDestroy(): void {
        // Aborta o upload em andamento para não disparar alertas em outra tela
        if (this.isUploading) {
            this.uploadSubscription?.unsubscribe();
            this.finishUpload();
        }
    }

    public defaults() {
        return {
            id: '',
        };
    }

    async searchResponsiblesIsProcess() {
        let condition = `"USR_UsuarioInterno" = true AND "PE_CodigoPessoa" != ${this.global.user().PE_CodigoPessoa}`

        if (this.userService.isDiretorDepartamento()) {
            condition = '"PE_CodigoDepartamento" = ' + this.global.user().PE_CodigoDepartamento
        }

        this.userService.listAll(condition, 0).subscribe({
            next: (response: any) => {
                this.responsibles = response.data;
            }
        })
    }

    getProcess(id: number) {
        this.processService.getEntity(id).subscribe({
            next: async (response: IGetEntityProccessResponse) => {
                this.process = response.entity;
            }
        });
    }

    public getInternalProcess(id: number) {
        this.internalProcessService.getEntity(id).subscribe({
            next: (response: any) => {
                this.internalProcess = response.entity;
            },
        });
    }

    async searchResponsiblesIsInternalProcess() {
        if (this.userService.isDiretorDepartamento()) {
            this.userService.listAll('"PE_CodigoDepartamento" = ' + this.global.user().PE_CodigoDepartamento, 0).subscribe({
                next: (response: any) => {
                    this.responsibles = response.data
                }
            });
        }
    }


    
    blockLimitFiles() {
        const validFiles = this.allFiles.filter( file => file.status != this.canceledStatus )
        if (this.limitFiles && validFiles.length >= this.limitFiles) {
            this.global.swal.fire({
                title: 'Erro ao fazer o upload.',
                text: 'Limite de arquivos excedido.',
                icon: 'warning',
                showCancelButton: true,
                showConfirmButton: false,
                cancelButtonText: 'Ok',
            });
            return false;
        }

        return true;
    }

    droppedFiles(file: any, organization?: string) {
        if (this.isUploading) return;

        if (this.global.verifyFile(file, this.mimeTypes, this.maxFileSize)) {
            if (this.blockLimitFiles()) {
                this.allFiles.push(file[0]);
                this.upload(file[0], organization);
            }
        }
    }

    selectArquivo(file: any, organization?: string) {
        const files = file.srcElement.files;

        if (!this.isUploading && this.global.verifyFile(files, this.mimeTypes, this.maxFileSize)) {
            if (this.blockLimitFiles()) {
                this.allFiles.push(files[0]);
                this.upload(files[0], organization);
            }
        }

        // Limpa sempre para que selecionar o mesmo arquivo de novo dispare o change
        file.srcElement.value = '';
    }

    isFileUploading(file: any): boolean {
        return this.isUploading && file === this.uploadingFile;
    }

    deleteFromArray(idx: number) {

        const file = this.allFiles[idx];

        if (this.isFileUploading(file)) return;

        if (this.mappedFields) {
            this.allFiles.splice(idx, 1);
            
            this.deleteUploadedFile.emit({
                index: idx,
                typeDocument: this.typeDocument,
                ...file
            })

            if (this.removeFilesAfterSave) {
                this.allFiles = []
            }                    
        }
        else {
            this.allFiles.splice(idx, 1);
    
            this.deleteUploadedFile.emit({
                index: idx,
                typeDocument: this.typeDocument,
                nameUploaded: file.nameUploaded,
                allFiles: this.allFiles,
                ...file
            });
        }
    }

    upload(file: File, organization?: string) {
        this.isUploading = true;
        this.uploadProgress = 0;
        this.uploadingFileName = file.name;
        this.uploadingFile = file;

        if (!this.showProgressBar) {
            this.global.startLoading(null, true);
        }

        this.uploadSubscription = this.uploadService.uploadWithProgress({ file, organization }).subscribe({
            next: (event: any) => {
                if (event.type === HttpEventType.UploadProgress) {
                    // event.total é o tamanho do corpo da requisição, informado pelo navegador
                    this.uploadProgressUnknown = !event.total;
                    this.uploadProgress = event.total ? Math.round((100 * event.loaded) / event.total) : 0;
                    return;
                }

                if (event.type !== HttpEventType.Response) return;

                const response: any = event.body;

                if (!response?.files?.length) {
                    this.finishUpload();
                    this.removeFromList(file);
                    this.global.swal.fire({
                        title: 'Erro ao fazer upload',
                        text: response?.message || 'O servidor não confirmou o recebimento do arquivo. Tente novamente.',
                        icon: 'error',
                        showCancelButton: true,
                        showConfirmButton: false,
                        cancelButtonText: 'Ok',
                    });
                    return;
                }

                this.finishUpload();

                (file as any).nameUploaded = response.files[0];

                if (this.showSwalAfterUpload) {
                    this.global.swal.fire({
                        title: 'Tudo certo!',
                        text: 'Seu arquivo foi carregado com sucesso!',
                        icon: 'success',
                    });
                }
                this.eventUploadedFile.emit({
                    file: {
                        url: response.files[0],
                        name: file.name,
                        type: file.type,
                        typeDocument: this.typeDocument,
                    },
                });

                if (this.mappedFields && this.removeFilesAfterSave) {
                    this.allFiles = []
                }
            },
            error: () => {
                this.finishUpload();
                this.removeFromList(file);
                this.global.swal.fire({
                    title: 'Erro ao fazer upload',
                    text: 'Falha ao enviar o arquivo. Verifique o tamanho e tente novamente.',
                    icon: 'error',
                    showCancelButton: true,
                    showConfirmButton: false,
                    cancelButtonText: 'Ok',
                });
            },
        });
    }

    private finishUpload() {
        this.isUploading = false;
        this.uploadProgress = 0;
        this.uploadProgressUnknown = false;
        this.uploadingFileName = '';
        this.uploadSubscription = undefined;
        this.uploadingFile = undefined;

        // Só encerra o blockUI quando foi este componente que o abriu
        if (!this.showProgressBar) {
            this.global.stopLoading();
        }
    }

    private removeFromList(file: File) {
        const idx = this.allFiles.indexOf(file);
        if (idx >= 0) {
            this.allFiles.splice(idx, 1);
        }
    }

    cancelUpload() {
        const file = this.uploadingFile;

        this.uploadSubscription?.unsubscribe();
        this.finishUpload();

        if (file) {
            this.removeFromList(file);
        }
    }

    renameFiles(file: any[]) {
        if (!this.mappedFields) {
            this.allFiles = this.files.map((file) => {
                return {
                    id: file.SOLA_CodigoSolicitacaoAnexo,
                    name: file.SOLA_Nome,
                    anexo: file.SOLA_Anexo,
                    status: file.SOLA_Status,
                    dataUpload: file.SOLA_DataUpload,
                };
            });
        }
        else {
            this.allFiles = this.files.map((file) => {
                return {
                    id: file[this.mappedFields[0]],
                    name: file[this.mappedFields[1]],
                    anexo: file[this.mappedFields[2]],
                    status: file[this.mappedFields[3]],
                    dataUpload: file[this.mappedFields[4]],
                    justificativa: file[this.mappedFields[5]],
                    dataAnulacao: file[this.mappedFields[6]]
                };
            });
        }
    }

    emitAdminDelete(idx: number, file: any) {
        this.adminDeleteFile.emit({
            index: idx,
            typeDocument: this.typeDocument,
            ...file
        });
    }

    anularDocumento(id: number, idx: number) {
        if (!this.userService.isCidadao() &&
            (
              this.process?.PRC_CodigoResponsavel === this.global.user().PE_CodigoPessoa ||
              this.internalProcess?.PI_CodigoResponsavel === this.global.user().PE_CodigoPessoa
            )
          ){
            Swal.fire({
                title: '<div class="d-flex align-items-center"><span class="badge badge-warning p-2 mr-3"><i class="ph ph-x text-lg"></i></span><h2 class="mb-0 text-default">Anular Documento</h2></div>',
                html: `
                    <div class="text-muted mb-3">
                        Antes de anular o documento, você deve detalhar abaixo o motivo pelo qual o documento está sendo anulado.
                    </div>
                `,
                input: 'textarea',
                inputAttributes: {
                    id: 'swal-input-textarea',
                    rows: '5',
                    style: 'width: 100%; border-radius: 8px; border: 1px solid #ccc; padding: 8px;'
                },
                inputPlaceholder: 'Descreva o motivo da anulação...',
                showCancelButton: true,
                confirmButtonText: 'Anular documento',
                cancelButtonText: 'Cancelar',
                confirmButtonColor: '#f5365c',
                inputValidator: (value) => {
                    if (!value || value.trim() === '') {
                        return 'É necessário informar o motivo da anulação.';
                    }
                    return null;
                }
            }).then((justification) => {
                if (!justification.isConfirmed || !justification.value) {
                    Swal.fire('Operação Cancelada', 'O arquivo não foi anulado.', 'info');
                    return;
                }
    
                this.allFiles[idx].justificativa = justification.value;
                this.confirmarAnulacaoDocumento(id, idx, justification.value);
            });
        } else {
            this.confirmarAnulacaoDocumento(id, idx, '');
        }
    }

    confirmarAnulacaoDocumento(id: number, idx: number, justificativa: any) {
        Swal.fire({
            title: 'Tem certeza que deseja anular este arquivo?',
            text: 'Esta ação não pode ser desfeita!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#417bff',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Sim',
            cancelButtonText: 'Não',
        }).then((result) => {
            if (!result.isConfirmed) {
                Swal.fire('Operação Cancelada', 'O arquivo não foi anulado.', 'info');
                return;
            }
    
            if (this.mappedFields) {
                // Quem escuta cancelFileUploaded faz o save e avisa no next: da requisição.
                // Não notificar aqui, senão o usuário recebe dois avisos de sucesso.
                this.cancelFileUploaded.emit({
                    index: idx,
                    typeDocument: this.typeDocument,
                    justificativa,
                    ...this.allFiles[idx]
                });

                this.allFiles[idx].status = 0;

                if (this.removeFilesAfterCancel) {
                    this.allFiles = [];
                }
            } else {
                const anexo = {
                    SOLA_Status: 0,
                    SOLA_CodigoSolicitacaoAnexo: id,
                    justificativa
                };
    
                this.anexoService.save(anexo).subscribe({
                    next: () => {
                        this.allFiles[idx].status = 0;
                        this.notifyDocumentCanceled();
                    },
                    error: (error: any) => {
                        console.error('Erro ao anular o arquivo:', error);
                        Swal.fire('Erro ao anular o arquivo', 'Ocorreu um erro ao anular o arquivo.', 'error');
                    },
                });
            }
        });
    }

    private notifyDocumentCanceled() {
        this.messageService.add({
            severity: 'success',
            icon: 'ph ph-check-circle',
            closable: true,
            summary: 'Sucesso!',
            detail: 'Documento anulado com sucesso.',
        });
    }

    download(name: string) {
        if (!name || this.downloadingFiles[name]) return;
        this.downloadingFiles[name] = true;
        this.uploadService.download(name, this.organization).subscribe({
            next: (response: any) => {
                this.downloadingFiles[name] = false;
                if (response.url) {
                    window.open(response.url, '_blank');
                }
            },
            error: (error: any) => {
                this.downloadingFiles[name] = false;
                console.error('Erro no download:', error);
            },
        });
    }

    isDownloading(name: string): boolean {
        return !!this.downloadingFiles[name];
    }
}
